/**
 * ============================================================================
 * [P3-01-C] FilterBar - 검색 및 필터링 컴포넌트
 *
 * 역할:
 * - 텍스트 검색 (debounce 300ms)
 * - 타입 필터 (전체/작성/작성 사용됨/레퍼런스/레퍼런스 사용됨) - 세그먼트 컨트롤 UI
 * - 정렬 옵션 (최신순/오래된순/제목순)
 *
 * 최적화:
 * - useMemo로 debounce 함수 메모이제이션
 * - useCallback으로 핸들러 메모이제이션
 * ============================================================================
 */

import { useState, useMemo, useCallback } from 'react'
import { debounce } from '@/utils'
import type { FilterState } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// 타입 필터 옵션 정의
// ============================================================================
const TYPE_FILTERS: { value: FilterState['type']; label: string; icon?: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'edit', label: '작성', icon: '✏️' },
  { value: 'edit-used', label: '작성 사용됨', icon: '✅' },
  { value: 'reference', label: '레퍼런스', icon: '📖' },
  { value: 'reference-used', label: '레퍼런스 사용됨', icon: '✅' },
]

// ============================================================================
// Props 타입 정의
// ============================================================================
interface FilterBarProps {
  onFilterChange: (filter: Partial<FilterState>) => void
  availableTopics?: string[]  // 사용 가능한 주제 목록
}

// ============================================================================
// Component
// ============================================================================
export function FilterBar({ onFilterChange, availableTopics = [] }: FilterBarProps) {
  // ========================================================================
  // 검색어 로컬 상태 (controlled input)
  // - 입력 즉시 UI 업데이트, 실제 필터는 debounce 후 적용
  // ========================================================================
  const [searchInput, setSearchInput] = useState('')
  const [activeType, setActiveType] = useState<FilterState['type']>('all')
  const [activeRefType, setActiveRefType] = useState<'all' | 'structure' | 'idea'>('all')

  // ========================================================================
  // Debounced 검색 함수
  // CRITICAL: useMemo로 debounce 함수를 메모이제이션
  // - onFilterChange가 변경될 때만 새 debounce 함수 생성
  // ========================================================================
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChange({ search: value })
      }, 300),
    [onFilterChange]
  )

  // ========================================================================
  // 검색 입력 핸들러
  // ========================================================================
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchInput(value)
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  // ========================================================================
  // 타입 필터 변경 핸들러 (세그먼트 버튼용)
  // ========================================================================
  const handleTypeClick = useCallback(
    (type: FilterState['type']) => {
      setActiveType(type)
      // 레퍼런스가 아닌 필터 선택 시 referenceType 리셋
      const isRefFilter = type === 'reference' || type === 'reference-used'
      if (!isRefFilter) {
        setActiveRefType('all')
        onFilterChange({ type, referenceType: undefined })
      } else {
        onFilterChange({ type, referenceType: activeRefType === 'all' ? undefined : activeRefType })
      }
    },
    [onFilterChange, activeRefType]
  )

  // ========================================================================
  // 레퍼런스 유형 필터 변경 핸들러 (구조/아이디어)
  // ========================================================================
  const handleRefTypeChange = useCallback(
    (refType: 'all' | 'structure' | 'idea') => {
      setActiveRefType(refType)
      onFilterChange({ referenceType: refType === 'all' ? undefined : refType })
    },
    [onFilterChange]
  )

  // ========================================================================
  // 정렬 변경 핸들러
  // ========================================================================
  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({ sortBy: e.target.value as FilterState['sortBy'] })
    },
    [onFilterChange]
  )

  // ========================================================================
  // 주제 필터 변경 핸들러
  // ========================================================================
  const handleTopicChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      onFilterChange({ topic: value === 'all' ? undefined : value })
    },
    [onFilterChange]
  )

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.filterBar}>
      {/* 검색 입력 */}
      <input
        type="text"
        className={styles.searchInput}
        placeholder="제목, 내용으로 검색..."
        value={searchInput}
        onChange={handleSearchChange}
        aria-label="검색"
      />

      {/* 세그먼트 컨트롤 (타입 필터) */}
      <div className={styles.segmentedControl} role="tablist" aria-label="저장된 글 필터">
        {TYPE_FILTERS.map(({ value, label, icon }) => (
          <button
            key={value}
            type="button"
            className={`${styles.segmentBtn} ${activeType === value ? styles.active : ''}`}
            onClick={() => handleTypeClick(value)}
            role="tab"
            aria-selected={activeType === value}
            aria-label={
              value === 'edit-used'
                ? '사용된 작성글만 표시'
                : value === 'reference-used'
                  ? '사용된 레퍼런스만 표시'
                  : undefined
            }
          >
            {icon && <span className={styles.segmentIcon}>{icon}</span>}
            {label}
          </button>
        ))}
      </div>

      {/* 레퍼런스 유형 필터 (레퍼런스/사용됨 필터 선택 시에만 표시) */}
      {(activeType === 'reference' || activeType === 'reference-used') && (
        <div className={styles.segmentedControl} role="group" aria-label="레퍼런스 유형 필터"
             style={{ gap: '4px' }}>
          <span style={{ fontWeight: 600, color: '#333', fontSize: '0.85rem', marginRight: '4px' }}>유형:</span>
          {[
            { value: 'all' as const, label: '전체' },
            { value: 'structure' as const, label: '📐 구조' },
            { value: 'idea' as const, label: '💡 아이디어' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.segmentBtn} ${activeRefType === value ? styles.active : ''}`}
              onClick={() => handleRefTypeChange(value)}
              style={{ fontSize: '0.85rem', padding: '4px 10px' }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* 주제 필터 */}
      {availableTopics.length > 0 && (
        <div className={styles.topicFilterGroup}>
          <label htmlFor="topic-filter" className={styles.topicFilterLabel}>
            주제 필터:
          </label>
          <select
            id="topic-filter"
            className={styles.filterSelect}
            onChange={handleTopicChange}
            defaultValue="all"
            aria-label="주제 필터"
          >
            <option value="all">전체 주제</option>
            {availableTopics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 정렬 옵션 */}
      <select
        className={styles.filterSelect}
        onChange={handleSortChange}
        defaultValue="newest"
        aria-label="정렬"
      >
        <option value="newest">최신순</option>
        <option value="oldest">오래된순</option>
        <option value="title">제목순</option>
        <option value="charCount">글자수순</option>
      </select>
    </div>
  )
}

export default FilterBar
